import { lerp } from '../../shared/scalar';
import { FIREFLY as FIREFLY_KIND } from '../../sim/full/attacks/swarm';
import { LANTERN_FIREFLY } from '../../sim/full/skills/lanterne';
import type { PlayerState, PropState, SimEvent, SimState } from '../../sim/state';
import { createBurster } from '../class-bursts';
import type { Family, RenderContext } from '../context';
import type { Frame } from '../frame';
import { TAU } from '../paint';
import { REFERENCE_RADIUS } from '../textures-looks';
import { byId, hide } from '../util';
import { ViewPool } from '../views';
import {
  FIREFLY,
  type FireflySprites,
  fireflySprites,
  orbitAt,
  placeFirefly,
  twinkle,
} from './veilleur';
import { LANTERN, UNIT } from './veilleur-paint';

const ORBIT_FADE = 40;
const WAVE = { across: 3, perBeat: 2 } as const;
const STING = { ticks: 6, from: 3, to: 12, peak: 0.8 } as const;
const LIT = { ticks: 10, from: 2, to: 9, peak: 0.9 } as const;
const OUT = { ticks: 18, from: 8, to: 2, peak: 0.8 } as const;
const OPEN_GLOW = { beats: 2, from: 16, to: 40, peak: 0.9 } as const;
const OPEN_RING = { beats: 1.5, from: 6, to: 40, peak: 0.8 } as const;
const HOMING_DIM = 0.4;
const ARC = { out: 18, back: -0.5, spread: 1.7 } as const;
const LANTERN_SIZE = 1.5;

interface Track {
  readonly playerId: number;
  phase: PropState['phase'];
  hitsLeft: number;
  x: number;
  y: number;
  seen: number;
}

export function createFireflies(ctx: RenderContext): Family {
  const { layers, textures } = ctx;
  const lanterns = new Set<string>();
  for (const [classId, effect] of ctx.skillEffects) {
    if (effect.kind === 'lanterne') {
      lanterns.add(classId);
    }
  }
  const swarms = new Set<string>();
  for (const [classId, attack] of ctx.attacks) {
    if (attack.kind === 'swarm') {
      swarms.add(classId);
    }
  }
  const burster = createBurster(ctx, 48);
  const tracks = new Map<number, Track>();
  const swarmSizes = new Map<number, number>();
  const spans = new Map<number, number>();
  let flyViews: ViewPool<FireflySprites> | undefined;
  let image = 0;

  function flyPool(): ViewPool<FireflySprites> {
    flyViews ??= new ViewPool<FireflySprites>(
      () => fireflySprites(layers.glow, layers.weapons, textures),
      (view) => {
        hide(view.halo, view.heart, view.core);
      },
    );
    return flyViews;
  }

  function trackOf(prop: PropState, x: number, y: number, frame: Frame, lit: boolean): Track {
    let track = tracks.get(prop.id);
    if (track === undefined) {
      track = {
        playerId: prop.playerId,
        phase: prop.phase,
        hitsLeft: prop.hitsLeft,
        x,
        y,
        seen: 0,
      };
      tracks.set(prop.id, track);
      if (lit && image > 1) {
        flare(LIT, x, y, frame);
      }
    }
    if (prop.hitsLeft < track.hitsLeft && prop.phase !== 'held') {
      flare(STING, prop.x, prop.y, frame);
    }
    track.phase = prop.phase;
    track.hitsLeft = prop.hitsLeft;
    track.x = x;
    track.y = y;
    track.seen = image;
    return track;
  }

  function flare(
    look: { ticks: number; from: number; to: number; peak: number },
    x: number,
    y: number,
    frame: Frame,
  ): void {
    burster.spawn(frame, {
      shape: textures.halo,
      now: frame.now,
      duration: look.ticks,
      x,
      y,
      fromRadius: look.from,
      toRadius: look.to,
      tint: frame.palette.noyau,
      peak: look.peak * frame.light.haloAlpha,
    });
  }

  function drawFirefly(
    state: SimState,
    prop: PropState,
    owner: PlayerState,
    count: number,
    alpha: number,
    frame: Frame,
  ): void {
    const { now, calm, tempo } = frame;
    const beats = now / tempo.ticksPerBeat;
    const size = owner.radius / REFERENCE_RADIUS;
    const unit = UNIT * size;
    const ownerX = lerp(owner.prevX, owner.x, alpha);
    const ownerY = lerp(owner.prevY, owner.y, alpha);
    const rank = prop.rank ?? 0;
    const orbit = orbitAt(rank, Math.max(1, count), beats, calm);
    if (prop.phase === 'held') {
      trackOf(prop, ownerX + orbit.along * unit, ownerY + orbit.across * unit, frame, true);
      return;
    }
    const simX = lerp(prop.prevX, prop.x, alpha);
    const simY = lerp(prop.prevY, prop.y, alpha);
    const away = Math.hypot(simX - ownerX, simY - ownerY);
    const hover = Math.max(0, 1 - away / ORBIT_FADE);
    const fromLantern = prop.kind === LANTERN_FIREFLY;
    const home = fromLantern ? lanternAt(owner) : orbit;
    const heading = Math.atan2(prop.y - prop.prevY, prop.x - prop.prevX);
    const arc = fromLantern
      ? Math.sin(rank * ARC.spread) * ARC.out * unit * arcShare(state, prop, simX, simY, away)
      : 0;
    const wave =
      arc +
      (calm
        ? 0
        : Math.sin(TAU * WAVE.perBeat * beats + prop.id) * WAVE.across * unit * (1 - hover));
    const x = simX + hover * home.along * unit - Math.sin(heading) * wave;
    const y = simY + hover * home.across * unit + Math.cos(heading) * wave;
    trackOf(prop, x, y, frame, !fromLantern);
    const homing = fromLantern && prop.phase === 'returning' ? 1 - HOMING_DIM * hover : 1;
    placeFirefly(
      flyPool().acquire(prop.id),
      x,
      y,
      fromLantern ? size * LANTERN_SIZE : size,
      twinkle(rank, beats, calm) * homing,
      frame,
      textures,
    );
  }

  function arcShare(state: SimState, prop: PropState, x: number, y: number, away: number): number {
    if (prop.phase === 'returning') {
      const span = Math.max(spans.get(prop.id) ?? 0, away);
      spans.set(prop.id, span);
      return span === 0 ? 0 : ARC.back * Math.sin((Math.PI * away) / span);
    }
    const target = byId(state.enemies, prop.targetId ?? -1);
    if (target === undefined) {
      return 0;
    }
    const left = Math.hypot(target.x - x, target.y - y);
    return away + left === 0 ? 0 : Math.sin((Math.PI * away) / (away + left));
  }

  function opened(event: SimEvent, state: SimState, frame: Frame): void {
    if (event.type !== 'skillUsed') {
      return;
    }
    const player = byId(state.players, event.playerId);
    if (player === undefined || !lanterns.has(player.classId)) {
      return;
    }
    const unit = UNIT * (player.radius / REFERENCE_RADIUS);
    const at = lanternAt(player);
    const base = {
      now: frame.now,
      x: at.along * unit,
      y: at.across * unit,
      anchor: 'player',
      anchorId: player.id,
    } as const;
    burster.spawn(frame, {
      ...base,
      shape: textures.halo,
      duration: OPEN_GLOW.beats * frame.tempo.ticksPerBeat,
      fromRadius: OPEN_GLOW.from * unit,
      toRadius: OPEN_GLOW.to * unit,
      tint: frame.palette.noyau,
      peak: OPEN_GLOW.peak * frame.light.haloAlpha,
    });
    burster.spawn(frame, {
      ...base,
      shape: textures.ring,
      duration: OPEN_RING.beats * frame.tempo.ticksPerBeat,
      fromRadius: OPEN_RING.from * unit,
      toRadius: OPEN_RING.to * unit,
      tint: FIREFLY.light,
      peak: OPEN_RING.peak,
    });
  }

  return {
    reset(): void {
      tracks.clear();
      spans.clear();
      burster.bursts.clear();
      flyViews?.releaseAll();
    },
    onEvent: opened,
    update(state: SimState, alpha: number, frame: Frame): void {
      if (swarms.size === 0 && lanterns.size === 0) {
        return;
      }
      image += 1;
      const flies = flyPool();
      flies.begin();
      swarmSizes.clear();
      for (const prop of state.props ?? []) {
        if (prop.kind === FIREFLY_KIND) {
          swarmSizes.set(prop.playerId, (swarmSizes.get(prop.playerId) ?? 0) + 1);
        }
      }
      for (const prop of state.props ?? []) {
        if (prop.kind !== FIREFLY_KIND && prop.kind !== LANTERN_FIREFLY) {
          continue;
        }
        const owner = byId(state.players, prop.playerId);
        if (owner !== undefined) {
          drawFirefly(state, prop, owner, swarmSizes.get(owner.id) ?? 1, alpha, frame);
        }
      }
      flies.end();
      for (const [id, track] of tracks) {
        if (track.seen === image) {
          continue;
        }
        tracks.delete(id);
        spans.delete(id);
        const owner = byId(state.players, track.playerId);
        if (owner !== undefined && track.phase === 'held') {
          flare(OUT, track.x, track.y, frame);
        }
      }
      burster.bursts.update(state, alpha, frame);
    },
    destroy(): void {
      burster.bursts.clear();
    },
  };
}

function lanternAt(player: PlayerState): { along: number; across: number } {
  const angle = Math.atan2(player.aim.y, player.aim.x);
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return {
    along: LANTERN.along * cos - LANTERN.across * sin,
    across: LANTERN.along * sin + LANTERN.across * cos,
  };
}
